import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AlertTriangle } from 'lucide-react'

interface BulkDeleteDialogProps {
  open: boolean
  onOpenChange: (_open: boolean) => void
  selectedCategories: string[]
  onBulkDelete: (_categoryIds: string[]) => Promise<void>
  isLoading?: boolean
}

export function BulkDeleteDialog({
  open,
  onOpenChange,
  selectedCategories,
  onBulkDelete,
  isLoading = false,
}: BulkDeleteDialogProps) {
  const handleConfirm = async () => {
    await onBulkDelete(selectedCategories)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Confirm Bulk Delete
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete {selectedCategories.length} selected categor
            {selectedCategories.length > 1 ? 'ies' : 'y'}? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
            <p className="text-sm text-red-800">
              <strong>Warning:</strong> Deleting categories will permanently remove them from the
              database. Products in these categories will be automatically moved to "Uncategorized".
            </p>
          </div>

          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              All products in the selected categories will be moved to the "Uncategorized" category.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={handleConfirm} disabled={isLoading}>
            {isLoading
              ? 'Deleting...'
              : `Delete ${selectedCategories.length} categor${selectedCategories.length > 1 ? 'ies' : 'y'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
